/**
 * Journey progress tracker (#150, FR-9.11) — the numbered-pills stepper shown to the
 * host on the create screen and through the pre-play phases (LOBBY/GENERATING).
 *
 * Presentational only: it reflects the current {@link JourneyStep} (computed by
 * {@link journeyStep}) and marks each step done / current / upcoming from its ordinal
 * position — it never drives progression and adds no action (FR-9.5). Mirrors the
 * ordinal done/active/pending pattern of {@link GenerationStatus}.
 */

import { JOURNEY_STEPS, type JourneyStep } from '../game/journey';

interface JourneyStepperProps {
  /** The host's current step; earlier steps render done, later ones upcoming. */
  current: JourneyStep;
}

export function JourneyStepper({ current }: JourneyStepperProps): React.JSX.Element {
  const currentIndex = Math.max(
    0,
    JOURNEY_STEPS.findIndex((s) => s.id === current),
  );

  return (
    <ol
      className="journey-stepper"
      data-testid="journey-stepper"
      aria-label="Game setup progress"
    >
      {JOURNEY_STEPS.map(({ id, label }, i) => {
        const state =
          i < currentIndex ? 'done' : i === currentIndex ? 'current' : 'upcoming';
        const isCurrent = state === 'current';
        return (
          <li
            key={id}
            className={`journey-step journey-step--${state}`}
            data-testid={`journey-step-${id}`}
            data-state={state}
            aria-current={isCurrent ? 'step' : undefined}
          >
            <span className="journey-step__dot" aria-hidden="true">
              {state === 'done' ? '✓' : i + 1}
            </span>
            <span className="journey-step__label">{label}</span>
          </li>
        );
      })}
    </ol>
  );
}
